import { BILLING_SOURCE } from '../../../../../bridge/events/types';
import { ONE_CHAR_GENDER } from '../../../../../pages/NewPatient';
import { AP } from '../../../../../pages/NewQueue';
import { getSelectedClinicId } from '../../../../../store/idbHandler';
import { Patient } from '../../../../../store/types/patient';
import { Prescription } from '../../../../../store/types/prescription';
import { getAgeString } from '../../../../../store/utils';

export const getPtDetailsStringFromPrescription = (prescription?: Prescription): string => {
    if (!prescription) return '';
    const { patient } = prescription;
    const { profile } = patient;
    const { personal } = profile;
    const { name, gender, age } = personal;
    const ageString = getAgeString(
        {
            age: age?.value,
            month: age?.month,
            dob: age?.dob,
        },
        undefined,
        '',
    );
    const ptDetails = [name, gender && ONE_CHAR_GENDER[gender.toLocaleLowerCase()], ageString]
        .filter(Boolean)
        .join(' | ');
    return ptDetails;
};

export const getPtDetailsStringFromAppointment = (appointment: AP): string => {
    const { patient } = appointment;
    const { fln: name, gen: gender, dob } = patient;
    const ageString = getAgeString({ dob: dob || '' });
    const ptDetails = [name, gender && ONE_CHAR_GENDER[gender.toLocaleLowerCase()], ageString]
        .filter(Boolean)
        .join(' | ');
    return ptDetails;
};

export const getPtDetailsStringFromPatient = (patient: Patient): string => {
    const { personal } = patient;
    const { name, gender, age } = personal;
    const { f, l } = name;
    const fullName = [f, l].filter(Boolean).join(' ');
    const ageString = getAgeString(
        {
            age: age?.value,
            month: age?.month,
            dob: age?.dob,
        },
        undefined,
        '',
    );
    const ptDetails = [fullName, gender && ONE_CHAR_GENDER[gender.toLocaleLowerCase()], ageString]
        .filter(Boolean)
        .join(' | ');
    return ptDetails;
};

export interface BillingPopupData {
    visitId: string;
    uhid: string;
    mobile?: string;
    uuid: string;
    fln: string;
    oid: string;
    clinicId: string;
    source: BILLING_SOURCE;
    aptDate: string;
}

export const getBillingPopupDataFromPrescription = (
    prescription?: Prescription,
): BillingPopupData => {
    if (!prescription) {
        return {
            visitId: '',
            uhid: '',
            mobile: '',
            uuid: '',
            fln: '',
            oid: '',
            clinicId: '',
            source: 'prescription',
            aptDate: '',
        };
    }
    const { patient, visitId, clinic, date } = prescription;
    const { localId, id, profile, formData } = patient;
    const { personal } = profile;
    const { name, phone } = personal;
    const patientId = localId || id || '';
    return {
        visitId: visitId,
        uhid: formData?.find((fd) => fd.key === 'uhid')?.value?.toString() || '',
        mobile: phone?.n,
        uuid: patientId,
        fln: name,
        oid: patientId,
        clinicId: clinic._id,
        source: 'prescription',
        aptDate: date.toISOString(),
    };
};

export const getBillingPopupDataFromAppointment = (appointment: AP): BillingPopupData => {
    const { aid, patient, date, formData, clinic_id } = appointment;
    const { mobile, fln } = patient;
    const patientId = appointment.patient_oid || patient.oid || '';
    return {
        visitId: aid,
        uhid: formData?.find((fd) => fd.key === 'uhid')?.value?.toString() || '',
        mobile: mobile,
        uuid: patientId,
        fln: fln || '',
        oid: patientId,
        clinicId: clinic_id,
        source: 'apt-queue',
        aptDate: date,
    };
};

export const getBillingPopupDataFromPatient = (patient: Patient): BillingPopupData => {
    const { personal, formData } = patient;
    const { name, phone } = personal;
    const { f, l } = name;
    const fullName = [f, l].filter(Boolean).join(' ');
    const patientId = patient?.localId || patient?.id || '';
    return {
        visitId: '',
        uhid: formData?.find((fd) => fd.key === 'uhid')?.value?.toString() || '',
        mobile: phone?.n,
        uuid: patientId,
        fln: fullName,
        oid: patientId,
        clinicId: getSelectedClinicId() || '',
        source: 'pt-details',
        aptDate: '',
    };
};
